import { OPTION_PAGINATION } from 'constants';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import _ from 'lodash';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { assignTherapist } from 'api/endpoints/children';
import { getListTherapist } from 'api/endpoints/therapist';
import Button from 'components/Button';
import { toastError, toastSuccess } from 'lib/toast';
import PopupAssignTherapistHeader from 'pages/Users/List/PopupAssignTherapist/PopupAssignTherapistHeader';
import PopupAssignTherapistPagination from 'pages/Users/List/PopupAssignTherapist/PopupAssignTherapistPagination';
import PopupAssignTherapistSearch from 'pages/Users/List/PopupAssignTherapist/PopupAssignTherapistSearch';
import PopupAssignTherapistTable from 'pages/Users/List/PopupAssignTherapist/PopupAssignTherapistTable';
import { setLoading } from 'store/actions/common';
import { convertToQueryString } from 'utils/helper';

const PopupAssignTherapist = ({ child, closePopup, reloadData }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { me } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.common);

  const modalRef = useRef(null);
  const [options, setOptions] = useState({ keyword: null, ...OPTION_PAGINATION });
  const [sort, setSort] = useState([]);
  const [listTherapists, setListTherapists] = useState([]);
  const [selectedRows, setSelectedRows] = useState([{ page: options.page, selected: [] }]);

  // SEARCH
  const onChangeKeyword = (keyword) => {
    if (keyword !== options.keyword) {
      setOptions((prevState) => {
        return { ...prevState, keyword: keyword };
      });
    }
  };

  // PAGINATION
  const onPageChange = (e) => {
    if (e !== options.page) {
      setOptions((prevState) => {
        return { ...prevState, page: e };
      });
      return true;
    }

    return false;
  };

  // SORT
  const onSortChange = (e) => {
    setSort(() => e);
  };

  /**
   * @name handleSort
   * @returns {string[]}
   * @example ['title', '-updated_at']
   * @example ['-title', 'updated_at']
   */
  const handleSort = () => {
    return (
      sort.map((item) => {
        return item.desc ? `-${item.id}` : item.id;
      }) || []
    );
  };

  const handleGetTherapist = async () => {
    const sortHandle = handleSort();
    const queryString = convertToQueryString({ keyword: options.keyword, sort_by: sortHandle, page: options.page });
    dispatch(setLoading({ isOpen: true }));
    try {
      await getListTherapist(queryString)
        .then((response) => {
          const totalPage = Math.ceil(response.total / response.page_size);
          const page = response.page > totalPage ? (totalPage === 0 ? 1 : totalPage) : options.page;

          setListTherapists(response?.data || []);
          setOptions((prevState) => {
            return {
              ...prevState,
              total: response.total,
              page_size: response.page_size,
              page: page,
            };
          });

          // SET DEFAULT SELECTED ROWS
          const selectedIds = child.therapists ? child.therapists.map((item) => item.id) : [];
          setSelectedRows((prevState) => {
            const cloned = _.cloneDeep(prevState);
            const currentPageIndex = cloned.findIndex((item) => item.page === options.page);

            if (currentPageIndex !== -1) {
              // page already exists → update selected
              cloned[currentPageIndex].selected = selectedIds;
            } else {
              // page does not exist → add new
              cloned.push({ page: options.page, selected: selectedIds });
            }

            return cloned;
          });
        })
        .catch(() => {
          dispatch(setLoading({ isOpen: false }));
        });
    } catch {
      dispatch(setLoading({ isOpen: false }));
    } finally {
      dispatch(setLoading({ isOpen: false }));
    }
  };

  // GET DATA
  const getTherapists = useCallback(async () => {
    if (me) {
      handleGetTherapist();
    }
  }, [options.page, options.keyword, sort, me]);

  useEffect(() => {
    getTherapists();
  }, [getTherapists]);

  // SET ASSIGN THERAPIST
  const onSetAssignTherapist = async () => {
    const therapistsSelected = selectedRows.map((row) => row.selected);
    const therapistIds = therapistsSelected.flat();
    if (therapistIds.length > 0) {
      dispatch(setLoading({ isOpen: true }));
      try {
        const response = await assignTherapist(child.id, therapistIds);
        toastSuccess(response.message);
        closePopup();
        reloadData?.();
      } catch (error) {
        toastError(error.message);
      } finally {
        dispatch(setLoading({ isOpen: false }));
      }
    } else {
      toastError(t('PLEASE_SELECT_THERAPIST'));
    }
  };

  useEffect(() => {
    document.documentElement.classList.add('overflow-hidden');
    document.documentElement.classList.add('h-screen');

    return () => {
      document.documentElement.classList.remove('overflow-hidden');
      document.documentElement.classList.remove('h-screen');
    };
  }, []);

  const clickBackdrop = () => {
    modalRef.current.classList.add('animate-fade-out');
    setTimeout(() => {
      modalRef.current.classList.remove('animate-fade-out');
    }, 300);
  };

  return ReactDOM.createPortal(
    <div data-testid="modal" id="modal" className="fixed inset-0 flex-middle-center bg-gray-800 bg-opacity-50 z-50">
      <div id="backdrop-modal" onClick={clickBackdrop} className="fixed inset-0 backdrop" />

      <div ref={modalRef} className={'bg-white rounded-sm shadow-lg relative max-w-[1064px] w-full modal-content max-h-[98vh] overflow-y-auto'}>
        <div data-testid="header-modal" id="header-modal" className="flex items-start py-4 px-6 border-b border-solid border-gray-651">
          <h2 className="text-base font-bold text-logo" dangerouslySetInnerHTML={{ __html: t('TITLE_ASSIGN_THERAPIST') }} />
        </div>

        <div data-testid="content-modal" id="content-modal" className="p-6">
          <PopupAssignTherapistHeader options={options} />
          <PopupAssignTherapistSearch options={options} onSubmit={onChangeKeyword} />
          <div className={'w-full mt-4'}>
            <PopupAssignTherapistTable
              data={listTherapists}
              setSort={onSortChange}
              sort={sort}
              page={options.page}
              keyword={options.keyword}
              isFilter={!!options.keyword || options.municipality_code}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
            />
          </div>
          {options.total > 0 && (
            <PopupAssignTherapistPagination options={options} onPageChange={onPageChange} loading={loading.isOpen} center={false} />
          )}
        </div>

        <div data-testid="footer-modal" id="footer-modal" className="px-6 py-4 border-t border-solid border-gray-651 flex justify-end gap-2">
          <Button text={t('CANCEL_TEXT')} color="outline" onClick={closePopup} />
          <Button text={t('SET_ASSIGN_THERAPIST')} color="primary" onClick={onSetAssignTherapist} />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default React.memo(PopupAssignTherapist);
